// @ts-nocheck - Complex type issues
/**
 * Daily Progress Service
 *
 * Manages daily learning statistics (New/Improved/Due words) with:
 * - Real-time database synchronization
 * - Cross-conversation persistence
 * - LocalStorage backup for resilience
 * - Midnight reset mechanism
 */

import { supabase } from './supabase';

export interface DailyProgressStats {
  newWordsToday: number;      // Words learned TODAY only
  improvedWordsToday: number;  // Words reviewed/improved TODAY only
  dueWords: number;           // Words due for review (global, live)
  dailyXpEarned: number;      // XP earned TODAY only (from user_xp.xp_today)
  lastUpdated: string;        // ISO timestamp of last update
}

export interface CachedDailyProgress extends DailyProgressStats {
  date: string;  // YYYY-MM-DD format for cache validation
  userId: string;
  language: string;
}

class DailyProgressService {
  private static instance: DailyProgressService;
  private cache: Map<string, CachedDailyProgress> = new Map();
  private updateCallbacks: Set<(stats: DailyProgressStats) => void> = new Set();
  private midnightTimerId: NodeJS.Timeout | null = null;

  private constructor() {
    this.setupMidnightReset();
    this.loadFromLocalStorage();

    // 🔥 TEMPORARY: Clear all caches on startup to force use of updated database functions
    // This ensures migration 038 fixes are immediately applied
    this.clearAllCaches();
  }

  static getInstance(): DailyProgressService {
    if (!DailyProgressService.instance) {
      DailyProgressService.instance = new DailyProgressService();
    }
    return DailyProgressService.instance;
  }

  /**
   * Fetch daily progress stats from the database
   */
  async fetchDailyProgress(userId: string, language: string, forceRefresh: boolean = false): Promise<DailyProgressStats> {
    try {
      // Check cache first (valid for current day only)
      const cacheKey = this.getCacheKey(userId, language);
      const cached = this.getFromCache(cacheKey);

      if (cached && this.isCacheValid(cached) && !forceRefresh) {
        return cached;
      }

      // Call all functions in parallel for optimal performance
      const [dailyStatsResponse, dueCardsResponse, xpResponse] = await Promise.all([
        supabase.rpc('get_daily_progress_stats', {
          p_user_id: userId,
          p_language_code: language
        }),
        supabase.rpc('get_srs_cards_due_for_review', {
          p_user_id: userId,
          p_language_code: language,
          p_limit: 1000
        }),
        // Fetch real XP from user_xp table (replaces hardcoded dummy value)
        supabase
          .from('user_xp')
          .select('xp_today, last_xp_gained_at')
          .eq('user_id', userId)
          .eq('language_code', language)
          .maybeSingle()
      ]);

      console.log('🔍 [DAILY PROGRESS DEBUG] Raw database responses:', {
        timestamp: new Date().toISOString(),
        dailyStats: dailyStatsResponse.data,
        dueCards: dueCardsResponse.data?.length,
        dailyStatsError: dailyStatsResponse.error,
        dueCardsError: dueCardsResponse.error,
        userId: userId.substring(0, 8),
        forceRefresh,
        cacheKey: this.getCacheKey(userId, language)
      });

      if (dailyStatsResponse.error) {
        console.error('Error fetching daily progress stats:', dailyStatsResponse.error);
        // Return cached data if available, even if stale
        if (cached) return cached;
        // Return default values
        return this.getDefaultStats();
      }

      // Handle daily stats response from RPC function
      const dailyStatsData = Array.isArray(dailyStatsResponse.data) ? dailyStatsResponse.data[0] : dailyStatsResponse.data;
      console.log('🔍 [DAILY PROGRESS DEBUG] Processed daily stats data:', dailyStatsData);

      if (!dailyStatsData) {
        console.error('❌ No data returned from daily progress stats function');
        return this.getDefaultStats();
      }

      // Get due words count from the SAME function that conversation planner uses
      const dueCardsCount = dueCardsResponse.error ? 0 : (dueCardsResponse.data?.length || 0);
      console.log('🔍 [DAILY PROGRESS DEBUG] Due words from get_srs_cards_due_for_review:', dueCardsCount);

      // Get real daily XP from user_xp (reset if last activity was a different day)
      let realXpToday = 0;
      if (!xpResponse.error && xpResponse.data) {
        const lastDate = xpResponse.data.last_xp_gained_at
          ? new Date(xpResponse.data.last_xp_gained_at).toISOString().split('T')[0]
          : null;
        const today = new Date().toISOString().split('T')[0];
        realXpToday = lastDate === today ? (xpResponse.data.xp_today || 0) : 0;
      }

      const stats: DailyProgressStats = {
        newWordsToday: dailyStatsData.new_words_today || 0,
        improvedWordsToday: dailyStatsData.improved_words_today || 0,
        dueWords: dueCardsCount, // ✅ Use the SAME function as conversation planner
        dailyXpEarned: realXpToday,
        lastUpdated: dailyStatsData.last_updated || new Date().toISOString()
      };

      console.log('🔍 [DAILY PROGRESS DEBUG] Final transformed stats (using get_srs_cards_due_for_review for due words):', stats);

      // Update cache and localStorage
      this.updateCache(cacheKey, userId, language, stats);

      // Notify all subscribers
      this.notifySubscribers(stats);

      return stats;
    } catch (error) {
      console.error('Unexpected error in fetchDailyProgress:', error);
      return this.getDefaultStats();
    }
  }

  /**
   * Quick validation with reduced database load (Phase 3)
   * Used for Tier 2 updates in progressive update system
   */
  async quickValidation(userId: string, language: string): Promise<DailyProgressStats> {
    try {
      console.log('🔍 Quick validation for user:', userId, 'language:', language);

      // Use lighter query that just gets counts without complex aggregations
      const { data, error } = await supabase.rpc('get_quick_daily_progress', {
        user_id: userId,
        target_language: language
      });

      if (error) {
        console.warn('Quick validation failed, falling back to cache:', error);
        const cacheKey = this.getCacheKey(userId, language);
        const cached = this.cache.get(cacheKey);
        return cached?.stats || this.getDefaultStats();
      }

      const quickStats: DailyProgressStats = {
        newWordsToday: data?.[0]?.new_words_today || 0,
        improvedWordsToday: data?.[0]?.improved_words_today || 0,
        dueWords: data?.[0]?.due_words || 0,
        dailyXpEarned: data?.[0]?.daily_xp_earned || 0,
        lastUpdated: new Date().toISOString()
      };

      console.log('✅ Quick validation completed:', quickStats);
      return quickStats;

    } catch (error) {
      console.warn('Quick validation error, using fallback:', error);
      const cacheKey = this.getCacheKey(userId, language);
      const cached = this.cache.get(cacheKey);
      return cached?.stats || this.getDefaultStats();
    }
  }

  /**
   * Refresh daily progress after SRS processing
   * Called after each message interaction
   */
  async refreshAfterSRSUpdate(userId: string, language: string): Promise<DailyProgressStats> {
    // Invalidate cache to force fresh fetch
    const cacheKey = this.getCacheKey(userId, language);
    this.cache.delete(cacheKey);

    // Fetch fresh data from database
    return this.fetchDailyProgress(userId, language);
  }

  /**
   * Subscribe to daily progress updates
   */
  subscribe(callback: (stats: DailyProgressStats) => void): () => void {
    this.updateCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  /**
   * Get optimistic update for immediate UI feedback
   */
  getOptimisticUpdate(
    currentStats: DailyProgressStats,
    srsMetadata?: { newWords?: number; improvedWords?: number; xpGained?: number }
  ): DailyProgressStats {
    if (!srsMetadata) return currentStats;

    return {
      ...currentStats,
      newWordsToday: currentStats.newWordsToday + (srsMetadata.newWords || 0),
      improvedWordsToday: currentStats.improvedWordsToday + (srsMetadata.improvedWords || 0),
      dailyXpEarned: currentStats.dailyXpEarned + (srsMetadata.xpGained || 0),
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Private helper methods
   */
  private getCacheKey(userId: string, language: string): string {
    return `${userId}-${language}`;
  }

  private getFromCache(cacheKey: string): CachedDailyProgress | null {
    return this.cache.get(cacheKey) || null;
  }

  private isCacheValid(cached: CachedDailyProgress): boolean {
    const today = new Date().toISOString().split('T')[0];
    return cached.date === today;
  }

  private updateCache(
    cacheKey: string,
    userId: string,
    language: string,
    stats: DailyProgressStats
  ): void {
    const today = new Date().toISOString().split('T')[0];
    const cachedData: CachedDailyProgress = {
      ...stats,
      date: today,
      userId,
      language
    };

    this.cache.set(cacheKey, cachedData);
    this.saveToLocalStorage(cacheKey, cachedData);
  }

  private notifySubscribers(stats: DailyProgressStats): void {
    this.updateCallbacks.forEach(callback => {
      try {
        callback(stats);
      } catch (error) {
        console.error('Error in progress subscriber:', error);
      }
    });
  }

  private getDefaultStats(): DailyProgressStats {
    return {
      newWordsToday: 0,
      improvedWordsToday: 0,
      dueWords: 0,
      dailyXpEarned: 0,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * LocalStorage backup for resilience
   */
  private saveToLocalStorage(cacheKey: string, data: CachedDailyProgress): void {
    try {
      const storageKey = `daily-progress-${cacheKey}`;
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save daily progress to localStorage:', error);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith('daily-progress-'));

      keys.forEach(storageKey => {
        const data = localStorage.getItem(storageKey);
        if (data) {
          const cached = JSON.parse(data) as CachedDailyProgress;
          const cacheKey = storageKey.replace('daily-progress-', '');

          // Only load if it's today's data
          if (this.isCacheValid(cached)) {
            this.cache.set(cacheKey, cached);
          } else {
            // Clean up old data
            localStorage.removeItem(storageKey);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load daily progress from localStorage:', error);
    }
  }

  /**
   * Midnight reset mechanism
   */
  private setupMidnightReset(): void {
    const scheduleNextReset = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const msUntilMidnight = tomorrow.getTime() - now.getTime();

      this.midnightTimerId = setTimeout(() => {
        this.handleMidnightReset();
        scheduleNextReset(); // Schedule next reset
      }, msUntilMidnight);
    };

    scheduleNextReset();
  }

  private handleMidnightReset(): void {
    // Clear all caches
    this.cache.clear();

    // Clear localStorage
    const keys = Object.keys(localStorage).filter(key => key.startsWith('daily-progress-'));
    keys.forEach(key => localStorage.removeItem(key));

    // Notify all subscribers with reset stats
    this.notifySubscribers(this.getDefaultStats());

    console.log('Daily progress reset at midnight');
  }

  /**
   * Debug method to force refresh data (for testing)
   */
  async forceRefresh(userId: string, language: string): Promise<DailyProgressStats> {
    console.log('🚀 Force refreshing daily progress data...');

    // Clear cache
    const cacheKey = this.getCacheKey(userId, language);
    this.cache.delete(cacheKey);
    localStorage.removeItem(`daily-progress-${cacheKey}`);

    // Fetch fresh data
    return this.fetchDailyProgress(userId, language, true);
  }

  /**
   * Clear ALL caches to force refresh after database function updates
   */
  clearAllCaches(): void {
    console.log('🗑️ Clearing all daily progress caches due to database function update');

    // Clear in-memory cache
    this.cache.clear();

    // Clear localStorage cache
    const keys = Object.keys(localStorage).filter(key => key.startsWith('daily-progress-'));
    keys.forEach(key => localStorage.removeItem(key));

    // Notify all subscribers with reset stats to trigger refresh
    this.notifySubscribers(this.getDefaultStats());

    console.log('✅ All caches cleared, next fetch will use new database functions');
  }

  /**
   * Cleanup method for component unmounting
   */
  cleanup(): void {
    if (this.midnightTimerId) {
      clearTimeout(this.midnightTimerId);
      this.midnightTimerId = null;
    }
    this.updateCallbacks.clear();
  }
}

// Export singleton instance
export const dailyProgressService = DailyProgressService.getInstance();